import { useEffect, useState } from "react";
import { dataApi, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard, SectionCard } from "@/components/Primitives";
import { Users, UserCheck, CalendarClock, Gauge, FolderKanban, AlertTriangle, TrendingUp, Wallet, Activity, Sparkles, Clock, ListChecks, Ticket, TicketCheck } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, RadialBarChart, RadialBar, Legend,
} from "recharts";
import { motion } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const CHART_COLORS = ["hsl(211 100% 55%)", "hsl(262 83% 62%)", "hsl(162 63% 45%)", "hsl(32 95% 55%)", "hsl(340 82% 58%)"];
const STATUS_COLORS = {
  todo: "hsl(220 8% 55%)",
  in_progress: "hsl(211 100% 55%)",
  code_review: "hsl(262 83% 62%)",
  testing: "hsl(32 95% 55%)",
  completed: "hsl(152 68% 45%)",
};

function GlassTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-strong rounded-lg px-3 py-2 text-xs shadow-lg">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    dataApi.dashboard().then((r) => setData(r.data));
    if (!isClient) {
      dataApi.aiInsights().then((r) => setInsights(r.data.insights)).catch(() => {});
    }
  }, [isClient]);

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Executive Dashboard" subtitle="Loading organization pulse…" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="glass rounded-2xl h-32 shimmer" />
          ))}
        </div>
      </div>
    );
  }

  const k = data.kpis;

  if (isClient) {
    return (
      <div className="space-y-8">
        <PageHeader
          testid="dashboard-header"
          title="Client Dashboard"
          subtitle="Live status of your engaged projects"
        />

        {/* Client KPI Grid — only the metrics they care about */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KpiCard testid="kpi-total-projects"    index={0} variant="pink"    label="Total Projects"   value={k.total_projects}    icon={FolderKanban} />
          <KpiCard testid="kpi-active-projects"   index={1} variant="blue"    label="Active Projects"  value={k.active_projects}   icon={FolderKanban} />
          <KpiCard testid="kpi-total-tasks"       index={2} variant="indigo"  label="Total Tasks"      value={k.total_tasks}       icon={ListChecks} />
          <KpiCard testid="kpi-total-tickets"     index={3} variant="violet"  label="Total Tickets"    value={k.total_tickets}     icon={Ticket} />
          <KpiCard testid="kpi-pending-tickets"   index={4} variant={k.pending_tickets > 0 ? "amber" : "emerald"} label="Pending Tickets" value={k.pending_tickets} icon={TicketCheck} />
        </div>

        {/* Project Health + Upcoming Deadlines + Open Risks */}
        <div className="grid lg:grid-cols-3 gap-6">
          <SectionCard testid="chart-projects" title="Project Health" subtitle="Active portfolio" className="lg:col-span-2">
            <div className="space-y-3">
              {data.project_health.length === 0 && (
                <div className="text-sm text-muted-foreground">No projects assigned yet.</div>
              )}
              {data.project_health.map((p, i) => (
                <div key={p.name} data-testid={`project-row-${i}`} className="flex items-center gap-4">
                  <div className="w-40 shrink-0 text-sm font-medium truncate">{p.name}</div>
                  <div className="flex-1">
                    <Progress value={p.progress} className="h-2" />
                  </div>
                  <div className="w-16 text-right">
                    <div className="number text-sm font-medium">{p.progress}%</div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`w-16 justify-center ${p.health >= 80 ? "border-emerald-500/40 text-emerald-500" : p.health >= 60 ? "border-amber-500/40 text-amber-500" : "border-red-500/40 text-red-500"}`}
                  >
                    {p.health}
                  </Badge>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard testid="upcoming-deadlines" title="Upcoming Deadlines" subtitle="Next 30 days">
            <div className="space-y-2">
              {data.upcoming_deadlines.length === 0 && <div className="text-sm text-muted-foreground">No upcoming deadlines.</div>}
              {data.upcoming_deadlines.map((t) => (
                <div key={t.id} className="flex items-center gap-3 py-1.5">
                  <div className={`h-2 w-2 rounded-full ${t.priority === "critical" ? "bg-red-500" : t.priority === "high" ? "bg-amber-500" : "bg-primary"}`} />
                  <div className="flex-1 min-w-0 text-sm truncate">{t.title}</div>
                  <div className="text-xs text-muted-foreground number">{t.due}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        testid="dashboard-header"
        title="Executive Dashboard"
        subtitle="Real-time pulse across your entire organisation"
      />

      {/* KPI Grid — prismatic palette inspired by the Prizm logo */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard testid="kpi-employees"      index={0} variant="indigo"  label="Total Employees"    value={k.total_employees}    icon={Users}         delta={3.2} deltaLabel="MoM" />
        <KpiCard testid="kpi-attendance"     index={1} variant="cyan"    label="Avg Attendance"     value={k.avg_attendance}     suffix="%" icon={CalendarClock} delta={1.4} />
        <KpiCard testid="kpi-productivity"   index={2} variant="violet"  label="Productivity"       value={k.avg_productivity}   suffix="%" icon={Gauge}         delta={2.1} />
        <KpiCard testid="kpi-health"         index={3} variant="emerald" label="Org Health"         value={k.org_health_score}   suffix="/100" icon={Activity}   delta={0.8} />
        <KpiCard testid="kpi-projects"       index={4} variant="blue"    label="Active Projects"    value={k.active_projects}    icon={FolderKanban}  delta={0} />
        <KpiCard testid="kpi-overdue"        index={5} variant={k.overdue_tasks > 20 ? "rose" : "amber"} label="Overdue Tasks" value={k.overdue_tasks} icon={Clock} delta={-2.4} />
        <KpiCard testid="kpi-risks"          index={6} variant="rose"    label="Critical Risks"     value={k.critical_risks}     icon={AlertTriangle} delta={-1.2} />
        <KpiCard testid="kpi-budget"         index={7} variant={k.budget_utilization > 90 ? "orange" : "lime"} label="Budget Utilization" value={k.budget_utilization} suffix="%" icon={Wallet} delta={4.5} />
        <KpiCard testid="kpi-total-projects" index={8} variant="pink"    label="Total Projects"     value={k.total_projects}     icon={FolderKanban} />
        <KpiCard testid="kpi-active-emp"     index={9} variant="emerald" label="Active Employees"   value={k.active_employees}   icon={UserCheck} />
      </div>

      {/* AI Insights */}
      {insights.length > 0 && (
        <SectionCard
          testid="ai-insights-card"
          title="AI Suggested Actions"
          subtitle="Continuously analysed by Claude Sonnet 4.6"
          actions={<Badge variant="outline" className="gap-1.5"><Sparkles className="w-3 h-3" /> Live</Badge>}
        >
          <div className="grid md:grid-cols-2 gap-3">
            {insights.map((ins, i) => {
              const levelColor = {
                critical: "border-red-500/30 bg-red-500/5",
                warning: "border-amber-500/30 bg-amber-500/5",
                info: "border-primary/30 bg-primary/5",
                success: "border-emerald-500/30 bg-emerald-500/5",
              }[ins.level];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`ai-insight-${i}`}
                  className={`rounded-xl border p-4 ${levelColor}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-background flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{ins.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{ins.detail}</div>
                      {ins.entities?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {ins.entities.map((e) => (
                            <Badge key={e} variant="secondary" className="text-[10px] font-normal">{e}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Row: Attendance trend + Productivity + Health gauge */}
      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard testid="chart-attendance" title="Attendance Trend" subtitle="Last 14 days" className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.attendance_trend}>
                <defs>
                  <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(211 100% 55%)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(211 100% 55%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip content={<GlassTooltip />} />
                <Area type="monotone" dataKey="rate" stroke="hsl(211 100% 55%)" strokeWidth={2.5} fill="url(#attGrad)" name="Attendance %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard testid="chart-health" title="Organisation Health" subtitle="Composite score">
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ name: "Health", value: k.org_health_score, fill: "hsl(211 100% 55%)" }]} startAngle={90} endAngle={-270}>
                <RadialBar background dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute pointer-events-none text-center">
              <div className="number text-4xl font-medium">{k.org_health_score}</div>
              <div className="text-xs text-muted-foreground">out of 100</div>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Row: Productivity trend + Task status */}
      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard testid="chart-productivity" title="Productivity Trend" subtitle="8-week rolling" className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.productivity_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} domain={[60, 100]} />
                <Tooltip content={<GlassTooltip />} />
                <Line type="monotone" dataKey="productivity" stroke="hsl(262 83% 62%)" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(262 83% 62%)" }} name="Productivity" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard testid="chart-tasks" title="Task Status" subtitle="Across all projects">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.task_status}
                  dataKey="count"
                  nameKey="status"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                >
                  {data.task_status.map((s, i) => (
                    <Cell key={i} fill={STATUS_COLORS[s.status] || CHART_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip content={<GlassTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  iconType="circle"
                  formatter={(v) => v.replace("_", " ")}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Row: Project health + Top performers */}
      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard testid="chart-projects" title="Project Health" subtitle="Active portfolio" className="lg:col-span-2">
          <div className="space-y-3">
            {data.project_health.map((p, i) => (
              <div key={p.name} data-testid={`project-row-${i}`} className="flex items-center gap-4">
                <div className="w-40 shrink-0 text-sm font-medium truncate">{p.name}</div>
                <div className="flex-1">
                  <Progress value={p.progress} className="h-2" />
                </div>
                <div className="w-16 text-right">
                  <div className="number text-sm font-medium">{p.progress}%</div>
                </div>
                <Badge
                  variant="outline"
                  className={`w-16 justify-center ${p.health >= 80 ? "border-emerald-500/40 text-emerald-500" : p.health >= 60 ? "border-amber-500/40 text-amber-500" : "border-red-500/40 text-red-500"}`}
                >
                  {p.health}
                </Badge>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard testid="top-performers" title="Top Performers" subtitle="By productivity">
          <div className="space-y-3">
            {data.top_performers.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3">
                <div className="w-6 text-center number text-sm text-muted-foreground">{i + 1}</div>
                <Avatar className="h-9 w-9">
                  <AvatarImage src={authedUrl(p.avatar_url)} />
                  <AvatarFallback>{p.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                </div>
                <Badge variant="secondary" className="number">{p.score}</Badge>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Row: Upcoming + Risks */}
      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard testid="upcoming-deadlines" title="Upcoming Deadlines" subtitle="Next 30 days">
          <div className="space-y-2">
            {data.upcoming_deadlines.length === 0 && <div className="text-sm text-muted-foreground">No upcoming deadlines.</div>}
            {data.upcoming_deadlines.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-1.5">
                <div className={`h-2 w-2 rounded-full ${t.priority === "critical" ? "bg-red-500" : t.priority === "high" ? "bg-amber-500" : "bg-primary"}`} />
                <div className="flex-1 min-w-0 text-sm truncate">{t.title}</div>
                <div className="text-xs text-muted-foreground number">{t.due}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard testid="risks-summary" title="Open Risks" subtitle="Requires attention">
          <div className="space-y-2">
            {data.risks_summary.length === 0 && <div className="text-sm text-muted-foreground">No open risks.</div>}
            {data.risks_summary.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-1.5">
                <AlertTriangle className={`w-4 h-4 ${r.level === "critical" ? "text-red-500" : r.level === "high" ? "text-amber-500" : "text-muted-foreground"}`} />
                <div className="flex-1 min-w-0 text-sm truncate">{r.title}</div>
                <Badge variant="outline" className="capitalize text-[10px]">{r.category.replace("_", " ")}</Badge>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
